import {
  BASE_USER_CONFIG_API_URL,
  getUserConfigApiUrl,
  markUserConfigExplainerVideoSeen,
  parseUserConfigDto,
  toUserConfigSavePayload,
  type UserConfig
} from '@/entities/userConfig';
import { setUser } from '@/services/analytics';
import { AxiosError, http } from '@/services/http';
import { getStagingSuffix } from '@/utils/environmentPaths';

const USER_CONFIG_STORAGE_KEY = 'userConfig';

type SaveUserConfigOptions = {
  userEmail: string;
  previousHomepage: string;
};

const postUserConfig = (userConfig: UserConfig) =>
  http.internal.post(getUserConfigApiUrl(), {
    [USER_CONFIG_STORAGE_KEY]: toUserConfigSavePayload(userConfig)
  });

const fetchCurrentUserConfig = async (): Promise<UserConfig> => {
  const { data } = await http.internal.get(`${getUserConfigApiUrl()}/${USER_CONFIG_STORAGE_KEY}`);
  return parseUserConfigDto(data);
};

const migrateUserConfigFromBaseEndpoint = async (): Promise<UserConfig> => {
  const { data } = await http.internal.get(
    `${BASE_USER_CONFIG_API_URL}/${USER_CONFIG_STORAGE_KEY}`
  );
  const userConfig = parseUserConfigDto(data);
  await postUserConfig(userConfig);
  return userConfig;
};

const patchPreferredMarket = (
  userConfig: UserConfig,
  { userEmail, previousHomepage }: SaveUserConfigOptions
): Promise<unknown> | null => {
  const newHomepage = userConfig.homepage.value;

  if (newHomepage === previousHomepage || userEmail.length === 0) {
    return null;
  }

  return http.go.patch(`/users/${userEmail}`, {
    prefMarket: getStagingSuffix(newHomepage)
  });
};

const trackUserConfigSaved = (userConfig: UserConfig) => {
  setUser({
    defaultMarket: userConfig.defaultMarket.label,
    homepage: userConfig.homepage.label
  });
};

export const userConfigApi = {
  fetch: async (): Promise<UserConfig> => {
    try {
      return await fetchCurrentUserConfig();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return migrateUserConfigFromBaseEndpoint();
      }

      throw error;
    }
  },

  save: async (userConfig: UserConfig, saveOptions: SaveUserConfigOptions): Promise<UserConfig> => {
    const preferredMarketPatch = patchPreferredMarket(userConfig, saveOptions);
    const requests = [postUserConfig(userConfig), preferredMarketPatch].filter(
      (request): request is Promise<unknown> => request !== null
    );

    await Promise.all(requests);
    trackUserConfigSaved(userConfig);
    return userConfig;
  },

  markExplainerVideoSeen: async (userConfig: UserConfig): Promise<void> => {
    await postUserConfig(markUserConfigExplainerVideoSeen(userConfig));
  }
};
